import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'antd';
import { OfferResponse } from 'logic/api-models/api-models';
import { offerSlice } from 'logic/store/offer/offer.slice';
import { useInProgress } from 'logic/store/tracker/tracker.hooks';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { colors } from 'styles/colors';
import { getAuthenticatedUser } from 'logic/store/authentication/authentication.selectors';
import { navigationSlice } from 'logic/store/navigation/navigation.slice';
import { AppRoute } from 'app/app.route.const';
import { requestTutoringSlice } from 'logic/store/request-tutoring/request-tutoring.slice';
import getIsTutoringSchool from 'logic/helpers/isTutoringSchool';
import isPayPerLead from 'logic/helpers/isPayPerLead';
import { getLinkSrc } from 'logic/utils/link';
import { isElitetutor, isTrialableOffer } from 'logic/helpers/tutorHelper';
import { useLocation } from 'react-use';
import { Text } from 'components/text/Text';
import { useLocalizedI18nGlobal } from 'logic/translations/use-localized-i18n-global';

export const RequestLessonButton: React.FC<{ offer: OfferResponse; isRefugeeTutor: boolean }> = ({
  offer,
  isRefugeeTutor,
}) => {
  useLocalizedI18nGlobal();
  const dispatch = useDispatch();
  const TutoringSchool = getIsTutoringSchool(offer);
  const location = useLocation();
  const _isElitetutor = isElitetutor(offer, location?.pathname);
  const _isTrailable = isTrialableOffer(offer);

  const handleRequestLesson = useCallback(() => {
    dispatch(requestTutoringSlice.actions.initRequestTutoringFlow({ offer, isRefugeeTutor }));
  }, [dispatch]);

  const handleRedirect = (url: string, openInNewTab: boolean) => {
    dispatch(
      navigationSlice.actions.navigateTo({ path: getLinkSrc(url), openInNewTab: openInNewTab })
    );
  };

  const renderButton = () => {
    if (isPayPerLead(offer) && !_isElitetutor)
      return (
        <PrimaryButton
          onClick={() => handleRedirect(offer?.tutoringSchool?.websiteUrl || AppRoute.Home, true)}
        >
          {translate(translationKeys.buttons.visitWebsite)}
        </PrimaryButton>
      );
    if (_isElitetutor)
      return _isTrailable ? (
        <PrimaryButton
          style={{ height: 'auto', paddingBottom: 0 }}
          href={AppRoute.External_TrialHour_Offer}
        >
          <Text color={'white'} size={16}>
            {translate(translationKeys.buttons.trialEliteSeeker)}
          </Text>
        </PrimaryButton>
      ) : (
        <PrimaryButton
          style={{ height: 'auto', paddingBottom: 0 }}
          href={AppRoute.BookEliteTutor.replace(':offerID', offer?._id || '')}
        >
          <Text color={'white'} size={16}>
            {translate(translationKeys.buttons.bookALesson)}
          </Text>
        </PrimaryButton>
      );
    return (
      <PrimaryButton onClick={handleRequestLesson}>
        {TutoringSchool
          ? translate(translationKeys.buttons.sendRequest)
          : translate(translationKeys.buttons.requestLessons)}
      </PrimaryButton>
    );
  };

  return renderButton();
};

export const EditOfferButton: React.FC<{ offer?: OfferResponse }> = ({ offer }) => {
  const offerId = offer?._id;
  const dispatch = useDispatch();

  const handleEditOffer = useCallback(
    () => offerId && dispatch(navigationSlice.actions.navigateTo({ path: AppRoute.EditOffer })),
    [dispatch, offerId]
  );

  return (
    <LargeButton onClick={handleEditOffer}>
      {translate(translationKeys.buttons.editOffer)}
    </LargeButton>
  );
};

export const OfferPdfButton: React.FC = () => {
  const dispatch = useDispatch();

  const handleNavigateToOfferPdf = useCallback(
    () =>
      dispatch(
        navigationSlice.actions.navigateTo({ path: AppRoute.MyOfferPdf, openInNewTab: true })
      ),
    [dispatch]
  );

  return (
    <LargeButton onClick={handleNavigateToOfferPdf}>
      {translate(translationKeys.buttons.offerPdf)}
    </LargeButton>
  );
};

export const ActivationButton: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(getAuthenticatedUser);
  const offerId = user?.offer?._id;
  const isUpdateInProgress = useInProgress(offerSlice.actions.updateOfferStatus);
  const isPublished = user?.offer?.isPublished;

  if (!offerId) return null;

  const activateOffer = () => {
    dispatch(offerSlice.actions.updateOfferStatus({ offerId, isPublished: true }));
  };

  const deactivateOffer = () => {
    dispatch(offerSlice.actions.updateOfferStatus({ offerId, isPublished: false }));
  };

  return isPublished ? (
    <PrimaryButton
      onClick={deactivateOffer}
      disabled={isUpdateInProgress}
      loading={isUpdateInProgress}
    >
      {translate(translationKeys.buttons.deactivate)}
    </PrimaryButton>
  ) : (
    <ActivateButton
      onClick={activateOffer}
      disabled={isUpdateInProgress}
      loading={isUpdateInProgress}
    >
      {translate(translationKeys.buttons.activate)}
    </ActivateButton>
  );
};

const LargeButton = styled(Button).attrs({ size: 'large' })`
  margin-top: 16px;
`;

const PrimaryButton = styled(Button).attrs({ type: 'primary', size: 'large' })`
  margin: 16px 0;
`;

const ActivateButton = styled(PrimaryButton)`
  background-color: ${colors.green};
  border-color: ${colors.green};

  &:hover,
  &:active,
  &:focus {
    background-color: ${colors.darkGreen};
    border-color: ${colors.darkGreen};
  }
`;
